import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTheme } from 'styled-components';

import { CardErrorState } from '@/components';
import { useIsMobile } from '@/hooks';

import { chartColors } from '../../../config/colors';
import { ChartLoadingIcon } from '../../Loaders';
import { EmptyState } from '../EmptyState';

interface HsComposedChartProps {
  chartData: any[];
  isLoading?: boolean;
  error?: any;
  onRetry?: () => void;
  bars?: Array<{ key: string; name: string; color: string }>;
  lines?: Array<{ key: string; name: string; color: string }>;
}

const CustomTooltip: FC<any> = ({ active, payload, label, isDark }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="backdrop-blur-sm px-4 py-3 rounded-md shadow-lg border"
        style={{
          background: isDark ? 'rgba(36, 42, 51, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          borderColor: isDark ? '#374151' : '#e5e7eb',
        }}
      >
        <p className="text-sm font-light mb-2" style={{ color: isDark ? '#f8fafc' : '#111827' }}>{label}</p>
        {payload.map((item: any) => (
          <div key={item.dataKey} className="flex items-center gap-2 mb-1">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: item.color }}
            />
            <p className="text-sm font-medium" style={{ color: item.color }}>
              {item.name}
              :
              {' '}
              <span className="font-normal">{item.value?.toLocaleString()}</span>
            </p>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const CustomLegend: FC<any> = ({ payload, isDark }) => (
  <div className="flex flex-wrap gap-4 justify-center mt-4">
    {payload.map((entry: any) => (
      <div key={entry.dataKey} className="flex items-center gap-2">
        <div
          className="w-3 h-3 rounded-full"
          style={{ backgroundColor: entry.color }}
        />
        <span className="text-sm font-medium" style={{ color: isDark ? '#d1d5db' : '#374151' }}>{entry.value}</span>
      </div>
    ))}
  </div>
);

const HsComposedChartComponent: FC<HsComposedChartProps> = ({
  chartData,
  isLoading = false,
  error,
  onRetry,
  bars = [],
  lines = [],
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.mode === 'dark';
  const isMobile = useIsMobile();
  const hasData = chartData && chartData.length > 0;
  const hasNonZeroData = hasData && chartData.some((row) => row.total > 0);

  const gridColor = isDark ? '#374151' : '#e5e7eb';
  const axisColor = isDark ? '#9ca3af' : '#9ca3af';

  const chartHeight = isMobile ? 250 : 400;
  const chartMargin = isMobile
    ? { top: 10, right: 10, left: -20, bottom: 10 }
    : { top: 20, right: 30, left: 0, bottom: 20 };

  if (isLoading) {
    return (
      <div
        className="w-full flex flex-col items-center justify-center rounded-md"
        style={{
          height: isMobile ? '300px' : '445px',
          background: theme.colors.chart.background,
          borderColor: isDark ? '#374151' : 'rgba(226, 232, 240, 0.5)',
        }}
      >
        <ChartLoadingIcon size={isMobile ? 'sm' : 'lg'} />
        <p className="mt-4 text-xs sm:text-sm" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>{t('dashboard.loadingChartData')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="w-full flex items-center justify-center rounded-md border"
        style={{
          height: isMobile ? '300px' : '445px',
          background: theme.colors.chart.background,
          borderColor: isDark ? '#374151' : 'rgba(226, 232, 240, 0.5)',
        }}
      >
        <CardErrorState
          title={t('dashboard.chartError', 'Nie udało się wczytać wykresu')}
          error={error}
          onRetry={onRetry}
        />
      </div>
    );
  }

  return (
    <div
      className="w-full h-full rounded-md p-3 sm:p-6 shadow-sm border"
      style={{
        background: theme.colors.chart.background,
        borderColor: isDark ? '#374151' : 'rgba(226, 232, 240, 0.5)',
      }}
    >
      {!hasNonZeroData ? (
        <EmptyState />
      ) : (
        <ResponsiveContainer width="100%" height={chartHeight}>
          <ComposedChart
            data={chartData}
            margin={chartMargin}
          >
            <defs>
              {bars.map((bar, index) => (
                <linearGradient
                  key={`bar-gradient-${bar.key}`}
                  id={`barGradient-${index}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={bar.color} stopOpacity={1} />
                  <stop offset="100%" stopColor={bar.color} stopOpacity={0.8} />
                </linearGradient>
              ))}

              <filter id="composedShadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.2" />
              </filter>
            </defs>

            <CartesianGrid
              strokeDasharray="3 3"
              stroke={gridColor}
              vertical={false}
              style={{ opacity: 0.5 }}
            />

            <XAxis
              dataKey="name"
              stroke={axisColor}
              style={{ fontSize: isMobile ? '10px' : '12px', fontWeight: 500 }}
              axisLine={{ stroke: gridColor }}
              tickLine={{ stroke: gridColor }}
              height={chartData.length > 14 ? 12 : (isMobile ? 40 : 60)}
              padding={{ left: isMobile ? 5 : 20, right: isMobile ? 5 : 20 }}
              tick={chartData.length > 14 ? false : undefined}
              interval={chartData.length > 30 ? 'preserveStartEnd' : (isMobile ? 1 : 0)}
            />

            <YAxis
              stroke={axisColor}
              style={{ fontSize: isMobile ? '10px' : '12px', fontWeight: 500 }}
              axisLine={{ stroke: gridColor }}
              tickLine={{ stroke: gridColor }}
              width={isMobile ? 30 : 60}
            />

            <Tooltip
              content={<CustomTooltip isDark={isDark} />}
              cursor={{ fill: chartColors.primary, opacity: 0.1 }}
              wrapperStyle={{ pointerEvents: 'none', zIndex: 20 }}
            />

            <Legend content={<CustomLegend isDark={isDark} />} />

            {bars.map((bar, index) => (
              <Bar
                key={bar.key}
                dataKey={bar.key}
                stackId="events"
                fill={`url(#barGradient-${index})`}
                name={bar.name}
                barSize={isMobile ? 30 : 50}
                radius={index === bars.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]}
                filter={index === bars.length - 1 ? 'url(#composedShadow)' : undefined}
                isAnimationActive={false}
                animationDuration={800}
              />
            ))}

            {lines.map((line) => (
              <Line
                key={line.key}
                type="monotone"
                dataKey={line.key}
                stroke={line.color}
                name={line.name}
                strokeWidth={3}
                dot={{ r: 4, fill: line.color, strokeWidth: 2, stroke: isDark ? '#1a1f26' : '#ffffff' }}
                activeDot={{ r: 6, fill: line.color, strokeWidth: 2, stroke: isDark ? '#1a1f26' : '#ffffff' }}
                isAnimationActive={false}
                animationDuration={800}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export const HsComposedChart = memo(HsComposedChartComponent, (prevProps, nextProps) => (
  prevProps.isLoading === nextProps.isLoading
      && prevProps.chartData === nextProps.chartData
      && prevProps.bars === nextProps.bars
      && prevProps.lines === nextProps.lines
));
